import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CallbackErrorAxios,
  CallbackWarningAxios,
  AxiosService,
  RootState,
  SafetyCross,
  UUID,
} from '@umanforge/common';
import { useDispatch, useSelector } from 'react-redux';
import { ToastService } from 'services/toast.service';
import { ChartSectionService } from 'services/chartSection.service';
import { ChartOfSectionService } from 'services/chartOfSection.service';
import type { ChartSectionApi } from 'interfaces/chartSection.interface';
import type { ChartWithDataApi } from 'interfaces/chartWithData.interface';
import { ChartSectionsHeader } from './chartSectionsHeader.component';
import ChartSectionsStatus from './chartSectionsStatus.component';
import ChartDisplay from './chartDisplay.component';
import { NotArchivedEngagementsTable } from './notArchivedEngagementsTable.component';
import { EventManager } from 'interfaces/eventManager.interface';

type EventManagerChartsProps = {
  eventManager: EventManager;
  onValidate?: () => void;
  isAdminOfEventManager?: boolean;
  sectionsRefreshKey?: number;
};

const BREAKPOINT_SIDE_BY_SIDE = 900;

const EventManagerCharts: React.FC<EventManagerChartsProps> = ( {
  eventManager,
  onValidate,
  isAdminOfEventManager,
  sectionsRefreshKey = 0,
} ) => {
  const toastService = new ToastService( 'EventManagerCharts' );
  const dispatch     = useDispatch();
  const token        = useSelector( ( state: RootState ) => {return state.token.value;} );
  const me           = useSelector( ( state: RootState ) => {return state.user?.value;} );

  const [ chartSections,    setChartSections ]    = useState<ChartSectionApi[]>( [] );
  const [ selectedSectionId, setSelectedSectionId ] = useState<UUID | null>( null );
  const [ isLoadingSections, setIsLoadingSections ] = useState( false );
  const [ chartsOfSection,  setChartsOfSection ]  = useState<ChartWithDataApi[]>( [] );
  const [ chartsRefreshKey, setChartsRefreshKey ] = useState( 0 );
  const [ containerWidth, setContainerWidth ] = useState( () => { return typeof window !== 'undefined' ? window.innerWidth : 0; } );

  const selectedSectionIdRef = useRef<UUID | null>( null );
  const outerRef             = useRef<HTMLDivElement>( null );
  const tokenRef             = useRef( token );
  useEffect( () => { tokenRef.current = token; }, [ token ] );

  useEffect( () => {
    const el = outerRef.current;
    if ( !el ) {return;}

    const apply = ( w: number ) => {return setContainerWidth( w );};
    apply( el.getBoundingClientRect().width );

    if ( typeof ResizeObserver === 'undefined' ) {
      const onResize = () => {return apply( el.getBoundingClientRect().width );};
      window.addEventListener( 'resize', onResize );
      return () => {return window.removeEventListener( 'resize', onResize );};
    }

    let rafId: number | null = null;
    const ro = new ResizeObserver( entries => {
      const w = entries[ 0 ]?.contentRect.width ?? el.getBoundingClientRect().width;
      if ( rafId !== null ) {cancelAnimationFrame( rafId );}
      rafId = requestAnimationFrame( () => {return apply( w );} );
    } );
    ro.observe( el );

    return () => {
      if ( rafId !== null ) {cancelAnimationFrame( rafId );}
      ro.disconnect();
    };
  }, [] );

  const sideBySide = containerWidth >= BREAKPOINT_SIDE_BY_SIDE;

  const handleSafetyCrossValidate = ( success?: boolean ) => {
    if ( success !== false ) {
      toastService.showSuccess( 'translation:safety_cross_update_success' );
      onValidate?.();
    }
  };

  const handleSafetyCrossWarning: CallbackWarningAxios = response =>
  {return toastService.showWarning( AxiosService.getAxiosMessage( response ) );};

  const handleSafetyCrossError: CallbackErrorAxios = error =>
  {return toastService.showError( AxiosService.getAxiosMessage( error ) );};

  const refreshChartsOfSection = () => { setChartsRefreshKey( k => { return k + 1; } ); };

  useEffect( () => {
    if ( !selectedSectionId ) { setChartsOfSection( [] ); return; }
    ChartOfSectionService.getChartDatasOfSection(
      selectedSectionId, tokenRef.current, dispatch,
      handleSafetyCrossError, handleSafetyCrossWarning,
    ).then( charts =>
    {return setChartsOfSection( [ ...charts ].sort( ( a, b ) => {return a.position - b.position;} ) );},
    );
  }, [ selectedSectionId, dispatch, chartsRefreshKey ] );

  useEffect( () => { selectedSectionIdRef.current = selectedSectionId; }, [ selectedSectionId ] );

  useEffect( () => {
    setIsLoadingSections( true );
    ChartSectionService.getByEventManager(
      eventManager.id, tokenRef.current, dispatch,
      handleSafetyCrossError, handleSafetyCrossWarning,
    )
      .then( sections => {
        const ordered = [ ...sections ].sort( ( a, b ) => {return a.position - b.position;} );
        setChartSections( ordered );
        const current     = selectedSectionIdRef.current;
        const stillExists = current !== null && ordered.some( s => {return s.id === current;} );
        setSelectedSectionId( stillExists ? current : ( ordered[ 0 ]?.id ?? null ) );
      } )
      .finally( () => {return setIsLoadingSections( false );} );
  }, [ dispatch, eventManager.id, eventManager, sectionsRefreshKey ] );

  const selectedSectionName = useMemo(
    () => {return chartSections.find( s => {return s.id === selectedSectionId;} )?.name ?? '';},
    [ chartSections, selectedSectionId ],
  );

  const chartNodes = useMemo(
    () => {return chartsOfSection.map( ( chart, idx ) => {return (
      <div key={chart.id ?? idx} className="min-w-0 w-full overflow-x-auto">
        <ChartDisplay chart={chart} onPing={ () => { setChartsRefreshKey( k => { return k + 1; } ); } } />
      </div>
    );} );},
    [ chartsOfSection ],
  );

  const chartManagerIds = useMemo( () => {return chartsOfSection.map( c => {return c.manager.id;} );},   [ chartsOfSection ] );

  return (
    <div ref={outerRef} className="w-full px-3 sm:px-6 py-4 space-y-6">

      {/* Header */}
      { !isLoadingSections && chartSections.length > 0 && (
        <ChartSectionsHeader
          chartSections={chartSections}
          selectedSectionId={selectedSectionId}
          onSelectSection={setSelectedSectionId}
        />
      ) }

      <div
        style={{
          display: 'flex',
          flexDirection: sideBySide ? 'row' : 'column',
          alignItems: sideBySide ? 'flex-start' : 'stretch',
          gap: sideBySide ? '3rem' : '2rem',
        }}
      >
        {/* Graphiques */}
        <div
          style={sideBySide ? { flex: '1 1 0', minWidth: 0 } : undefined}
          className={sideBySide ? '' : 'w-full'}
        >
          <ChartSectionsStatus
            isLoadingSections={isLoadingSections}
            hasSections={chartSections.length > 0}
            selectedSectionName={selectedSectionName}
            selectedSectionId={selectedSectionId}
            charts={chartNodes}
            chartsOfSection={chartsOfSection}
            chartManagerIds={chartManagerIds}
            currentUserId={me?.id}
            isAdminOfEventManager={isAdminOfEventManager}
            onRefresh={refreshChartsOfSection}
          />
        </div>

        <div
          style={
            sideBySide
              ? { flexShrink: 0, width: 500, position: 'sticky', top: '1rem', alignSelf: 'flex-start' }
              : { width: '100%', display: 'flex', justifyContent: 'center' }
          }
        >
          <SafetyCross
            disabled={!isAdminOfEventManager}
            eventManagerId={eventManager.id}
            onWarning={handleSafetyCrossWarning}
            onError={handleSafetyCrossError}
            onValidate={handleSafetyCrossValidate}
            canValidate={isAdminOfEventManager}
          />
        </div>
      </div>

      {/* Table */}
      { selectedSectionId && (
        <NotArchivedEngagementsTable
          key={ selectedSectionId }
          eventManager={ eventManager }
          chartSectionId={ selectedSectionId }
        />
      ) }

    </div>
  );
};

export { EventManagerCharts };